/**
 * Time helpers for the Tractor Metrics page. Every tractor has its own zone
 * (`timeline.tz`), so the page formats and parses in that zone, never the
 * browser's.
 */

const partsCache = new Map<string, Intl.DateTimeFormat>();

function formatter(tz: string): Intl.DateTimeFormat {
  let f = partsCache.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    partsCache.set(tz, f);
  }
  return f;
}

/** Wall-clock fields of `date` in `tz`. */
export function wallClock(date: Date, tz: string) {
  const parts: Record<string, number> = {};
  for (const p of formatter(tz).formatToParts(date)) {
    if (p.type !== "literal") parts[p.type] = Number(p.value);
  }
  // Intl renders midnight as hour 24 in some engines.
  if (parts.hour === 24) parts.hour = 0;
  return parts as {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
  };
}

/** The zone's UTC offset in milliseconds at `date`. */
export function offsetAt(date: Date, tz: string): number {
  const w = wallClock(date, tz);
  const asUtc = Date.UTC(
    w.year,
    w.month - 1,
    w.day,
    w.hour,
    w.minute,
    w.second,
  );
  return asUtc - Math.floor(date.getTime() / 1000) * 1000;
}

/**
 * A `datetime-local` value ("YYYY-MM-DDTHH:MM"), read as wall-clock time in
 * `tz`, as an ISO 8601 string with that zone's offset. Two passes settle the
 * offset across a DST change.
 */
export function wallTimeToIso(wall: string, tz: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(wall);
  if (!m) throw new Error(`not a datetime-local value: ${wall}`);
  const [y, mo, d, h, mi, s] = m.slice(1).map((v) => Number(v ?? 0));
  const guess = Date.UTC(y, mo - 1, d, h, mi, s || 0);
  let offset = offsetAt(new Date(guess), tz);
  offset = offsetAt(new Date(guess - offset), tz);
  const instant = new Date(guess - offset);
  const sign = offset >= 0 ? "+" : "-";
  const abs = Math.abs(offset) / 60000;
  const oh = String(Math.floor(abs / 60)).padStart(2, "0");
  const om = String(abs % 60).padStart(2, "0");
  const w = wallClock(instant, tz);
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${w.year}-${pad(w.month)}-${pad(w.day)}T${pad(w.hour)}:${pad(w.minute)}` +
    `:${pad(w.second)}${sign}${oh}:${om}`
  );
}

/** "HH:MM" or "HH:MM:SS" in `tz`. */
export function clock(iso: string, tz: string, seconds = false): string {
  const w = wallClock(new Date(iso), tz);
  const pad = (n: number) => String(n).padStart(2, "0");
  const base = `${pad(w.hour)}:${pad(w.minute)}`;
  return seconds ? `${base}:${pad(w.second)}` : base;
}

/** "YYYY-MM-DDTHH:MM" for a datetime-local input, from an instant in `tz`. */
export function wallTimeValue(iso: string, tz: string): string {
  const w = wallClock(new Date(iso), tz);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${w.year}-${pad(w.month)}-${pad(w.day)}T${pad(w.hour)}:${pad(w.minute)}`;
}

/** Hours with one decimal from seconds. */
export function hours(seconds: number | undefined): string {
  return ((seconds ?? 0) / 3600).toFixed(1);
}

/** The zone the activity store keeps every tractor in today. */
export const TRACTOR_TZ = "America/New_York";

/**
 * Today's date, YYYY-MM-DD, in the tractors' zone. The API's local days are
 * in that zone, so the page's "today" has to be too: a reader on the west
 * coast late in the evening still reaches the current Atlanta day, and the
 * "now" line still draws on it.
 */
export function todayIso(tz: string = TRACTOR_TZ): string {
  const d = new Date();
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(d);
  } catch {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }
}

/** The local date `days` after `iso` (negative for before), YYYY-MM-DD. */
export function shiftDay(iso: string, days: number): string {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + days);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
